/**
 * Call It In LLC — Driver App (Mobile-First)
 *
 * Two-leg workflow per task:
 *   Assigned → En Route → Arrived (PU) → Picked Up → En Route (DL) → Arrived → Delivered
 *
 * Three shift phases:
 *   1. pre-shift   — Enter starting odometer → POST /driver/shift/start
 *   2. on-shift    — Task board with status-cycling action buttons
 *                    Tasks fetched from GET /driver/tasks (JWT-identified, 30 s poll)
 *                    GPS geofencing auto-triggers "Arrived (PU)" when within 150 m of pickup
 *                    GPS geofencing auto-triggers "Arrived" when within 150 m of dropoff
 *                    Pickup proof gated by pickupProofRequirement (photo only)
 *                    Delivery proof gated by podRequirement on each task
 *   3. ending      — Enter ending odometer → POST /driver/shift/end
 *
 * Proof-of-Pickup gate (at Arrived (PU) → Picked Up):
 *   "none"  → confirmation unlocked immediately (no modal)
 *   "photo" → driver must capture a photo at the pickup location
 *
 * Proof-of-Delivery (POD) gate (at Arrived → Delivered):
 *   "none"                → delivery unlocked on GPS-confirmed arrival (no modal)
 *   "photo"               → driver must capture a photo before delivery
 *   "signature"           → driver must draw a signature before delivery
 *   "photo_and_signature" → both required
 *
 * PWA / background location note:
 *   watchPosition fires only while the browser tab is in the foreground.
 *   When the tab is backgrounded, location updates stop and auto-geofence does not fire.
 *   Drivers must keep the tab active or use manual confirmation buttons as fallback.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDButton from "../../../components/MDButton";
import MDInput   from "../../../components/MDInput";
import { getCurrentUser, logoutUser } from "../../../data/users";
import {
  startDriverShift,
  endDriverShift,
  getBackendCapabilities,
  getMyTasks,
  updateTaskStatus,
  submitPOD,
  submitPickupPOD,
  requestTaskOverride,
  updateMyLocation,
} from "../../../api/client";
import callitinLogo from "../../../assets/images/callitinlogo.png";
import {
  accuracyNote,
  checkGeofence,
  isWeakAccuracy,
  stopCoords,
} from "../../../utils/geofence";
import {
  isTerminalTaskStatus,
  normalizeTaskStatus,
  OVERRIDE_TYPE,
  TASK_STATUS,
} from "../../../constants/operations";

// ─── Procedure helpers ────────────────────────────────────────────────────────

/**
 * Extract procedure summary from a notes field encoded by the Create Case form.
 * Returns null for all existing records that predate the Procedure section.
 * Format stored: "[PROC] Left Hip – Primary | optional note"
 */
const parseProcedureSummary = (notesStr) => {
  if (!notesStr) return null;
  const match = String(notesStr).match(/^\[PROC\]\s*([^\n|]+)/);
  return match ? match[1].trim() : null;
};

// ─── Constants ────────────────────────────────────────────────────────────────
// Maps the task's CURRENT status (lower-case) → the button label + next status.
// "Arrived (PU)" and "Arrived" are NOT in CYCLE — they are triggered by the
// GPS geofence (watchPosition) or by manual validation buttons in TaskCard.
// "Picked Up" and "En Route (DL)" have dedicated handlers in handleStatusAction.
const CYCLE = {
  [normalizeTaskStatus(TASK_STATUS.ASSIGNED)]: {
    label: "Accept & Start Route", color: "info", next: TASK_STATUS.EN_ROUTE,
  },
  [normalizeTaskStatus(TASK_STATUS.EN_ROUTE)]: {
    label: "Confirm Pickup Arrival", color: "warning", next: TASK_STATUS.ARRIVED_PU,
  },
  [normalizeTaskStatus(TASK_STATUS.EN_ROUTE_DL)]: {
    label: "Confirm Arrival", color: "info", next: TASK_STATUS.ARRIVED,
  },
  [normalizeTaskStatus(TASK_STATUS.EMERGENCY_DISPATCH)]: {
    label: "Start Route", color: "error", next: TASK_STATUS.EN_ROUTE,
  },
};

// Friendly toast message after each driver action
const ACTION_MESSAGES = {
  [TASK_STATUS.EN_ROUTE]:    "Route started — heading to pickup",
  [TASK_STATUS.ARRIVED_PU]:  "Arrived at pickup location",
  [TASK_STATUS.PICKED_UP]:   "Pickup confirmed",
  [TASK_STATUS.EN_ROUTE_DL]: "Delivery leg started — heading to dropoff",
  [TASK_STATUS.ARRIVED]:     "Arrived at delivery location",
  [TASK_STATUS.DELIVERED]:   "Delivery confirmed — nice work",
};

// Step / progress context shown inside the task card (4-step two-leg flow)
const STEP_CONTEXT = {
  "assigned":           { step: "Step 1 of 4", context: "Ready to start route" },
  "emergency dispatch": { step: "Step 1 of 4", context: "Emergency — start route now" },
  "en route":           { step: "Step 1 of 4", context: "En route to pickup" },
  "arrived (pu)":       { step: "Step 2 of 4", context: "At pickup — confirm and load" },
  "picked up":          { step: "Step 3 of 4", context: "Loaded — start delivery leg" },
  "en route (dl)":      { step: "Step 3 of 4", context: "En route to delivery" },
  "arrived":            { step: "Step 4 of 4", context: "Arrived — ready to deliver" },
};

// POD human-readable labels
const POD_LABEL = {
  none:                "No Proof",
  photo:               "📷 Photo",
  signature:           "✍️ Signature",
  photo_and_signature: "📷 + ✍️",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
}

function mapsUrl(task) {
  const o = encodeURIComponent(task.pickupAddress  || "");
  const d = encodeURIComponent(task.dropoffAddress || "");
  return `https://www.google.com/maps/dir/?api=1&origin=${o}&destination=${d}`;
}

// Navigate to a specific stop from the driver's current position.
// Used inside the Next Stop box so the destination matches the current phase.
function nextStopMapsUrl(address) {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || "")}`;
}

// Geofence maths + accuracy-aware radius live in utils/geofence.js so this
// screen, the backend and the iOS app apply the same rule.

/**
 * One-shot GPS read for a geofenced action.
 * Resolves { lat, lng, accuracy } (accuracy in metres, may be null) or null.
 */
function readGpsPosition(timeoutMs = 8_000) {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null }),
      ()  => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    );
  });
}

/**
 * Shared geofence validation for pickup / arrival / delivery.
 * - No stored stop coordinates → BLOCKED (dispatcher override required).
 * - GPS unavailable → BLOCKED.
 * - Distance > accuracy-aware radius (150 m + min(accuracy, 100 m)) → BLOCKED.
 * Returns { allowed: bool, reason: string, coords: {lat,lng,accuracy}|null }
 * The coords (including accuracy) are sent to the backend, which applies the
 * identical rule — see utils/geofence.js.
 */
async function validateStopLocation(task, prefix, m) {
  const target = stopCoords(task, prefix);
  if (!target) {
    return { allowed: false, coords: null, reason: m.noCoords };
  }
  if (!navigator?.geolocation) {
    return { allowed: false, coords: null, reason: m.noGps };
  }
  const coords = await readGpsPosition();
  if (!coords) {
    return { allowed: false, coords: null, reason: m.gpsFailed };
  }
  const { distanceM, radiusM, isInside } = checkGeofence(coords, target);
  if (!isInside) {
    const hint = isWeakAccuracy(coords.accuracy)
      ? " GPS is very imprecise — enable Precise Location and move to open sky."
      : "";
    return {
      allowed: false,
      coords,
      reason: `${m.notAt} — ${Math.round(distanceM)} m from ${m.place}${accuracyNote(coords.accuracy)}. ${m.rule(Math.round(radiusM))}${hint}`,
    };
  }
  return { allowed: true, coords, reason: "geofence-ok" };
}

/** Validate the driver is at the task's dropoff before completing delivery. */
function validateDeliveryLocation(task) {
  return validateStopLocation(task, "dropoff", {
    noCoords:  "DELIVERY BLOCKED — Delivery coordinates are not set on this task. Contact dispatch to complete.",
    noGps:     "GPS UNAVAILABLE — Location access is required to complete delivery.",
    gpsFailed: "GPS UNAVAILABLE — Cannot confirm delivery location. Enable location access and retry.",
    notAt:     "NOT AT DELIVERY LOCATION",
    place:     "delivery address",
    rule:      (r) => `Must be within ${r} m.`,
  });
}

/** Validate the driver is at the task's pickup before confirming pickup / arrival at pickup. */
function validatePickupLocation(task) {
  return validateStopLocation(task, "pickup", {
    noCoords:  "PICKUP BLOCKED — Pickup coordinates are not set on this task. Contact dispatch to confirm pickup.",
    noGps:     "GPS UNAVAILABLE — Location access is required to confirm pickup.",
    gpsFailed: "GPS UNAVAILABLE — Cannot confirm pickup without location access. Enable location and retry.",
    notAt:     "NOT AT PICKUP LOCATION",
    place:     "pickup address",
    rule:      (r) => `Must be within ${r} m.`,
  });
}

/** Validate the driver is at the task's dropoff for arrival confirmation. */
function validateArrivalLocation(task) {
  return validateStopLocation(task, "dropoff", {
    noCoords:  "ARRIVAL BLOCKED — Delivery coordinates are not set on this task. Contact dispatch to confirm arrival.",
    noGps:     "GPS UNAVAILABLE — Location access is required to confirm arrival.",
    gpsFailed: "GPS UNAVAILABLE — Cannot confirm arrival without location access.",
    notAt:     "NOT AT DELIVERY LOCATION",
    place:     "address",
    rule:      (r) => `Move within ${r} m to confirm arrival.`,
  });
}

function getOverrideMeta(task, type) {
  const nested = task?.overrides?.[type];
  const fallbackState = type === "arrival" ? task?.arrivalOverrideState : task?.deliveryOverrideState;
  if (nested && typeof nested === "object") {
    return { type, ...nested, state: nested.state || fallbackState || "none" };
  }
  return { type, state: fallbackState || "none" };
}

function isOverrideRequested(task, type) {
  return getOverrideMeta(task, type).state === `${type}_override_requested`;
}

function isOverrideApproved(task, type) {
  return getOverrideMeta(task, type).state === `${type}_override_approved`;
}

/** Compress + base64-encode an image File to a JPEG ≤ 800 px wide. */
function resizeImageBase64(file, maxWidth = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const ratio  = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width  * ratio);
        canvas.height = Math.round(img.height * ratio);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.72));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ─── SignaturePad ─────────────────────────────────────────────────────────────
/**
 * Lightweight canvas-based signature pad.
 * Calls onChange(base64String | null) whenever the user draws or clears.
 */
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const hasStrokes = useRef(false);

  const getPos = (e, canvas) => {
    const rect  = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] ?? e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width  / rect.width),
      y: (touch.clientY - rect.top)  * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const pos    = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    drawing.current = true;
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    ctx.strokeStyle = "#1e293b";
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    hasStrokes.current = true;
  };

  const endDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    if (hasStrokes.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasStrokes.current = false;
    onChange(null);
  };

  return (
    <Box>
      <Box
        sx={{
          border: "1.5px solid #E2E8F0",
          borderRadius: "10px",
          background: "#FAFAFA",
          overflow: "hidden",
          cursor: "crosshair",
        }}
      >
        <canvas
          ref={canvasRef}
          width={560}
          height={180}
          style={{ display: "block", width: "100%", touchAction: "none" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.75} px={0.5}>
        <Typography variant="caption" color="text.secondary">
          Sign using your finger or stylus
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#6B7280", cursor: "pointer", textDecoration: "underline" }}
          onClick={clear}
        >
          Clear
        </Typography>
      </Box>
    </Box>
  );
}

// ─── PickupProofModal ─────────────────────────────────────────────────────────
/**
 * Lightweight modal for capturing a pickup photo before confirming Picked Up.
 * Only "photo" proof is supported at pickup (no signature required).
 * Calls POST /tasks/{id}/pod/pickup, then transitions to Picked Up.
 */
function PickupProofModal({ task, onClose, onPickedUp }) {
  const req        = (task?.pickupProofRequirement ?? "none").toLowerCase();
  const needsPhoto = req === "photo";

  const [photoData,  setPhotoData]  = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");

  const photoInputRef = useRef(null);

  useEffect(() => {
    setError("");
    setPhotoData(null);
  }, [task?.id]);

  const canSubmit = !needsPhoto || !!photoData;

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await resizeImageBase64(file);
      setPhotoData(b64);
    } catch {
      setError("Could not process the photo. Please try again.");
    }
  };

  const handleSubmit = async () => {
    if (needsPhoto && !photoData) {
      setError("Photo proof is required. Capture a photo before confirming pickup.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      // 1. Save pickup proof
      const podRes = await submitPickupPOD(task.id, { photoBase64: photoData });
      if (!podRes.ok) {
        const d = await podRes.json().catch(() => ({}));
        setError(d?.detail ?? `Failed to save pickup proof (HTTP ${podRes.status}).`);
        return;
      }

      // 2. Transition to Picked Up (backend skips geofence since task is in Arrived (PU))
      const statusRes = await updateTaskStatus(task.id, "Picked Up", null);
      if (statusRes.ok) {
        onPickedUp(task.id);
        onClose();
      } else {
        const d = await statusRes.json().catch(() => ({}));
        setError(d?.detail ?? "Proof saved, but status update failed.");
      }
    } catch {
      setError("Server unreachable. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={!!task}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px", mx: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Typography fontWeight={800} fontSize={16}>
          Pickup Confirmation
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {task?.pickupFacilityName || task?.pickupAddress || "—"}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={submitting}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {needsPhoto && (
          <Box mb={1.5}>
            <Chip
              label="📷 Photo Required"
              size="small"
              sx={{ background: "#FEF3C7", color: "#B45309", fontWeight: 700, fontSize: 11 }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {needsPhoto && (
          <Box mb={3}>
            <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: "0.06em",
              textTransform: "uppercase", color: "#374151" }}>
              Pickup Photo
            </Typography>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handlePhoto}
            />
            {photoData ? (
              <Box mt={1}>
                <Box
                  component="img"
                  src={photoData}
                  alt="Pickup proof"
                  sx={{ width: "100%", maxHeight: 200, objectFit: "cover",
                    borderRadius: "10px", border: "1.5px solid #FCD34D" }}
                />
                <Box display="flex" justifyContent="flex-end" mt={0.75}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => { setPhotoData(null); photoInputRef.current.value = ""; }}
                  >
                    Retake
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box mt={1}>
                <MDButton
                  variant="outlined"
                  color="warning"
                  fullWidth
                  onClick={() => photoInputRef.current?.click()}
                  sx={{ borderRadius: "10px", fontWeight: 700 }}
                >
                  📷 Take Pickup Photo
                </MDButton>
              </Box>
            )}
          </Box>
        )}

        <Box display="flex" gap={1.5} mt={1}>
          <MDButton
            variant="outlined"
            color="secondary"
            fullWidth
            disabled={submitting}
            onClick={onClose}
            sx={{ borderRadius: "10px", fontWeight: 700 }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="warning"
            fullWidth
            disabled={!canSubmit || submitting}
            onClick={handleSubmit}
            sx={{ borderRadius: "10px", fontWeight: 700 }}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : "Confirm Pickup"}
          </MDButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}


// ─── PODModal ─────────────────────────────────────────────────────────────────
/**
 * Full-screen-friendly dialog for capturing proof-of-delivery
 * before the task is marked Delivered.
 */
function PODModal({ task, onClose, onDelivered, onRequestOverride }) {
  const req       = (task?.podRequirement ?? "none").toLowerCase().replace(/-/g, "_");
  const needsPhoto = req === "photo" || req === "photo_and_signature";
  const needsSig   = req === "signature" || req === "photo_and_signature";

  const [photoData,     setPhotoData]     = useState(null);
  const [sigData,       setSigData]       = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState("");
  const [locationBlocked, setLocationBlocked] = useState(false);

  const photoInputRef = useRef(null);

  useEffect(() => {
    setLocationBlocked(false);
    setError("");
  }, [task?.id]);

  // recipientName is required whenever signature proof is required
  const canSubmit = (!needsPhoto || !!photoData)
    && (!needsSig || !!sigData)
    && (!needsSig || !!recipientName.trim());

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await resizeImageBase64(file);
      setPhotoData(b64);
    } catch {
      setError("Could not process the photo. Please try again.");
    }
  };

  const handleSubmit = async () => {
    // Defense-in-depth: independently re-validate all requirements at submission time.
    // This cannot be bypassed by UI state inconsistencies.
    if (needsPhoto && !photoData) {
      setError("Photo proof is required. Capture a photo before submitting.");
      return;
    }
    if (needsSig && !sigData) {
      setError("Recipient signature is required. Sign in the field above.");
      return;
    }
    if (needsSig && !recipientName.trim()) {
      setError("Recipient name is required. Enter the name of the person receiving the delivery.");
      return;
    }
    if (!canSubmit) return;
    setError("");
    setLocationBlocked(false);
    setSubmitting(true);
    try {
      // 0. Validate delivery location (geofence gate — same rule as non-POD delivery).
      //    This fires even inside the modal, so the modal cannot be used to bypass location.
      const locCheck = isOverrideApproved(task, "delivery")
        ? { allowed: true, coords: null, reason: "dispatcher-override-approved" }
        : await validateDeliveryLocation(task);
      if (!locCheck.allowed) {
        setError(locCheck.reason);
        setLocationBlocked(true);
        setSubmitting(false);
        return;
      }

      // 1. Save POD assets
      const podRes = await submitPOD(task.id, {
        photoBase64:     photoData,
        signatureBase64: sigData,
        recipientName:   recipientName.trim() || null,
      });
      if (!podRes.ok) {
        const d = await podRes.json().catch(() => ({}));
        setError(d?.detail ?? `Failed to save proof (HTTP ${podRes.status}).`);
        return;
      }

      // 2. Update task status to Delivered — include confirmed GPS coords in payload.
      const statusRes = await updateTaskStatus(task.id, "Delivered", locCheck.coords ?? {});
      if (statusRes.ok) {
        onDelivered(task.id);
        onClose();
      } else {
        const d = await statusRes.json().catch(() => ({}));
        setError(d?.detail ?? "POD saved, but status update failed.");
      }
    } catch {
      setError("Server unreachable. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={!!task}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px", mx: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Typography fontWeight={800} fontSize={16}>
          Proof of Delivery
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {task?.clientName || task?.dropoffAddress || "—"}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={submitting}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Requirement badge */}
        <Box mb={2}>
          <Chip
            label={POD_LABEL[req] ?? req}
            size="small"
            sx={{ background: "#DCFCE7", color: "#15803D", fontWeight: 700, fontSize: 11 }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {locationBlocked && !isOverrideApproved(task, "delivery") && (
          <Box mb={2}>
            {isOverrideRequested(task, "delivery") ? (
              <Chip size="small" color="warning" label="OVERRIDE REQUESTED — AWAITING DISPATCH" />
            ) : (
              <MDButton
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => {
                  onClose();
                  onRequestOverride(task, "delivery");
                }}
                sx={{ fontWeight: 700, borderRadius: "6px" }}
              >
                Request Dispatch Override
              </MDButton>
            )}
          </Box>
        )}

        {/* Photo section */}
        {needsPhoto && (
          <Box mb={3}>
            <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: "0.06em",
              textTransform: "uppercase", color: "#374151" }}>
              Photo Proof
            </Typography>

            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handlePhoto}
            />

            {photoData ? (
              <Box mt={1}>
                <Box
                  component="img"
                  src={photoData}
                  alt="POD photo"
                  sx={{ width: "100%", maxHeight: 220, objectFit: "cover",
                    borderRadius: "10px", border: "1.5px solid #86EFAC" }}
                />
                <Box display="flex" justifyContent="flex-end" mt={0.75}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => { setPhotoData(null); photoInputRef.current.value = ""; }}
                  >
                    Retake
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                mt={1}
                onClick={() => photoInputRef.current?.click()}
                sx={{
                  border: "2px dashed #CBD5E1",
                  borderRadius: "10px",
                  py: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  background: "#F8FAFC",
                  "&:hover": { background: "#F0F9FF", borderColor: "#7DD3FC" },
                }}
              >
                <Typography fontSize={28}>📷</Typography>
                <Typography variant="body2" fontWeight={700} mt={0.5}>
                  Tap to take photo
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or choose from library
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Signature section */}
        {needsSig && (
          <Box mb={2}>
            <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: "0.06em",
              textTransform: "uppercase", color: "#374151", display: "block", mb: 1 }}>
              Recipient Signature
            </Typography>
            <SignaturePad onChange={setSigData} />
            {sigData && (
              <Box mt={0.5} display="flex" alignItems="center" gap={0.75}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981" }} />
                <Typography variant="caption" color="#15803D" fontWeight={700}>
                  Signature captured
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Recipient name — required when signature is required */}
        {needsSig && (
          <Box mb={3}>
            <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: "0.06em",
              textTransform: "uppercase", color: "#374151", display: "block", mb: 1 }}>
              Recipient Name <Box component="span" sx={{ color: "#EF4444" }}>*</Box>
            </Typography>
            <MDInput
              fullWidth
              placeholder="Full name of person receiving delivery"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              inputProps={{ maxLength: 120 }}
              sx={{
                "& input": { fontSize: 14, fontWeight: 500 },
              }}
            />
            {recipientName.trim() && (
              <Box mt={0.5} display="flex" alignItems="center" gap={0.75}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981" }} />
                <Typography variant="caption" color="#15803D" fontWeight={700}>
                  Name recorded
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Submit */}
        <MDButton
          variant="gradient"
          color="success"
          fullWidth
          size="large"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
          sx={{ fontWeight: 800, borderRadius: "12px", py: 1.5 }}
        >
          {submitting
            ? <CircularProgress size={20} color="inherit" />
            : "Submit & Mark Delivered"
          }
        </MDButton>

        {!canSubmit && (
          <Box
            sx={{
              mt: 1.5, p: 1.25,
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderLeft: "3px solid #EF4444",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                textTransform: "uppercase", color: "#B91C1C", mb: 0.25,
              }}
            >
              DELIVERY BLOCKED — REQUIRED PROOF
            </Typography>
            {needsPhoto && !photoData && (
              <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-line" }}>
                {"CAPTURE REQUIRED PHOTO\nUse the camera above to proceed."}
              </Typography>
            )}
            {needsSig && !sigData && (
              <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-line" }}>
                {"CAPTURE RECIPIENT SIGNATURE\nSign in the field above to proceed."}
              </Typography>
            )}
            {needsSig && sigData && !recipientName.trim() && (
              <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-line" }}>
                {"ENTER RECIPIENT NAME\nType the full name of the person receiving the delivery."}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OverrideRequestDialog({ task, overrideType, onClose, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!task || !overrideType) return null;

  const label = overrideType === OVERRIDE_TYPE.ARRIVAL ? "Arrival"
              : overrideType === OVERRIDE_TYPE.PICKUP  ? "Pickup"
              : "Delivery";

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const capabilities = await getBackendCapabilities({ forceRefresh: true }).catch(() => null);
      if (capabilities?.routes?.taskOverrideRequest === false) {
        setError("Override system unavailable on the current backend deployment. Contact dispatch directly.");
        return;
      }

      const res = await requestTaskOverride(task.id, overrideType, reason.trim());
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onSubmitted(data?.task || task, overrideType);
        onClose();
      } else if (res.status === 404) {
        setError("Override system unavailable. Contact dispatch directly to unblock this task.");
      } else if (res.status === 403) {
        setError(data?.detail ?? "This task is not assigned to your driver account.");
      } else if (res.status === 429) {
        setError("Too many override requests. Please wait before submitting another.");
      } else {
        const detail = data?.detail;
        const msg = Array.isArray(detail)
          ? detail.map((e) => e.msg || e.message || String(e)).join("; ")
          : typeof detail === "string"
          ? detail
          : `Override request failed (HTTP ${res.status}).`;
        setError(msg);
      }
    } catch (err) {
      const msg = err?.message ? String(err.message) : "";
      const isNetworkError =
        !msg ||
        msg.toLowerCase().includes("failed to fetch") ||
        msg.toLowerCase().includes("networkerror") ||
        msg.toLowerCase().includes("network request failed") ||
        msg.toLowerCase().includes("load failed");
      setError(
        isNetworkError
          ? "Server unreachable — check your connection and retry."
          : msg || "Unexpected error. Please retry."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={!!task}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px", mx: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography fontWeight={800} fontSize={16}>
          Request Dispatch Override
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label} exception for {task?.clientName || task?.dropoffAddress || "task"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
          Dispatch can approve this exception for this task only. The task will stay blocked until approval.
        </Typography>
        <MDInput
          fullWidth
          multiline
          rows={3}
          label="Reason (optional)"
          placeholder={
            overrideType === "pickup"
              ? "GPS off, confirmed at vendor dock, address mismatch..."
              : "GPS off, hospital entrance mismatch, at receiving dock..."
          }
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 240 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          This request does not advance the task.
        </Typography>
      </DialogContent>

      <Box display="flex" gap={1.5} px={3} pb={3}>
        <MDButton
          variant="outlined"
          color="secondary"
          fullWidth
          disabled={submitting}
          onClick={onClose}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          fullWidth
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : "Send Request"}
        </MDButton>
      </Box>
    </Dialog>
  );
}

// ─── Logo ─────────────────────────────────────────────────────────────────────
function Logo({ sx }) {
  return (
    <Box component="img" src={callitinLogo} alt="Call It In"
      sx={{ height: 36, display: "block", ...sx }} />
  );
}

// ─── TaskCard ─────────────────────────────────────────────────────────────────
function TaskCard({
  task,
  onAction,
  onDeliverClick,
  onPickupDeliverClick,
  onRequestOverride,
  isUpdating,
  blockedReason = "",
  blockedArrivalReason = "",
  blockedPickupReason = "",
}) {
  const statusKey      = normalizeTaskStatus(task.status);
  const isArrived      = statusKey === normalizeTaskStatus(TASK_STATUS.ARRIVED);
  const isArrivedPU    = statusKey === normalizeTaskStatus(TASK_STATUS.ARRIVED_PU);
  const isPickedUp     = statusKey === normalizeTaskStatus(TASK_STATUS.PICKED_UP);
  const isEnRoute      = statusKey === normalizeTaskStatus(TASK_STATUS.EN_ROUTE);
  const isEnRouteDL    = statusKey === normalizeTaskStatus(TASK_STATUS.EN_ROUTE_DL);
  const isVault        = isTerminalTaskStatus(statusKey);
  const isCaseSync     = !!(task.linkedCaseId || task.caseNumber || task.trayCount);
  const isEmerg        = task.isEmergency || statusKey.includes("emergency");
  // Same coordinate reader the geofence validators use, so the UI never shows
  // an action the validator would reject as "coordinates not set".
  const hasDropoffCoords = stopCoords(task, "dropoff") !== null;
  const hasPickupCoords  = stopCoords(task, "pickup")  !== null;
  const arrivalOverrideRequested  = isOverrideRequested(task, "arrival");
  const arrivalOverrideApproved   = isOverrideApproved(task, "arrival");
  const deliveryOverrideRequested = isOverrideRequested(task, "delivery");
  const deliveryOverrideApproved  = isOverrideApproved(task, "delivery");
  const pickupOverrideRequested   = isOverrideRequested(task, "pickup");
  const pickupOverrideApproved    = isOverrideApproved(task, "pickup");

  const req            = (task.podRequirement         ?? "none").toLowerCase().replace(/-/g, "_");
  const pickupReq      = (task.pickupProofRequirement ?? "none").toLowerCase();
  const needsPOD       = req !== "none";
  const needsPickupPOD = pickupReq !== "none";

  // ── Two-leg cycleEntry resolution ─────────────────────────────────────────
  // Priority order: Arrived (delivery) > En Route (DL) > Arrived (PU) >
  //   Picked Up (start delivery leg) > En Route (pickup arrival) > CYCLE fallback
  const cycleEntry = isArrived
    ? {
        label:  blockedReason
          ? (needsPOD ? "Retry — Mark Delivered (POD)" : "Retry — Mark Delivered")
          : (needsPOD ? "Mark Delivered (POD)"         : "Mark Delivered"),
        color: "success",
        next:  "Delivered",
      }
    : isEnRouteDL && (hasDropoffCoords || arrivalOverrideApproved)
    ? {
        label: blockedArrivalReason ? "Retry — Confirm Arrival" : "Confirm Arrival",
        color: "info",
        next: "Arrived",
      }
    : isArrivedPU
    ? {
        label: needsPickupPOD ? "Confirm Pickup (Photo Req.)" : "Confirm Pickup",
        color: "warning",
        next: "Picked Up",
      }
    : isPickedUp
    ? {
        label: "Start Delivery Leg",
        color: "info",
        next: TASK_STATUS.EN_ROUTE_DL,
      }
    : isEnRoute && (hasPickupCoords || pickupOverrideApproved)
    ? {
        label: blockedPickupReason ? "Retry — Confirm Pickup Arrival" : "Confirm Pickup Arrival",
        color: "warning",
        next: TASK_STATUS.ARRIVED_PU,
      }
    : CYCLE[statusKey];

  const handleCycleClick = () => {
    if (!cycleEntry) return;
    if (isArrived && needsPOD) {
      onDeliverClick(task);
    } else if (isArrivedPU && needsPickupPOD) {
      onPickupDeliverClick(task);
    } else {
      onAction(task, cycleEntry.next);
    }
  };

  // ── Derived display values ─────────────────────────────────────────────────
  const clientDisplay = task.clientName || task.dropoffFacilityName || task.hospitalName || "—";

  // Mission title — operational route format: "Pickup Facility → Dropoff Facility"
  const pickupFacility  = task.pickupFacilityName || null;
  const dropoffFacility = task.dropoffFacilityName || task.hospitalName || task.clientName || clientDisplay;
  const missionTitle    = pickupFacility ? `${pickupFacility} → ${dropoffFacility}` : dropoffFacility;

  // Step / progress context — compact, operational wording
  const stepInfo = isVault ? null : STEP_CONTEXT[statusKey] ?? null;

  // Task type badge
  const taskType       = (task.taskType || "").toLowerCase();
  const taskTypeLabel  = taskType === "pickup" ? "PICKUP" : taskType === "delivery" ? "DELIVERY" : null;
  const taskTypeBg     = taskType === "pickup" ? "#DBEAFE" : "#EDE9FE";
  const taskTypeColor  = taskType === "pickup" ? "#1D4ED8" : "#6D28D9";

  // Status accent colour
  const accentColor = isVault     ? "#94A3B8"
    : isArrived                   ? "#10B981"
    : isArrivedPU                 ? "#F59E0B"  // orange = at pickup, confirming
    : isEmerg                     ? "#EF4444"
    :                               "#3B82F6";

  // Next stop depends on the current phase of the task
  const isPickupPhase  = [
    normalizeTaskStatus(TASK_STATUS.ASSIGNED),
    normalizeTaskStatus(TASK_STATUS.EN_ROUTE),
    normalizeTaskStatus(TASK_STATUS.EMERGENCY_DISPATCH),
    normalizeTaskStatus(TASK_STATUS.ARRIVED_PU),  // still at the pickup facility
  ].includes(statusKey);
  const isDropoffPhase = isPickedUp || isEnRouteDL || isArrived;
  const nextStopLabel  = isPickupPhase ? "PICKUP" : isDropoffPhase ? "DROPOFF" : null;
  const nextFacility   = isPickupPhase ? task.pickupFacilityName  : task.dropoffFacilityName;
  const nextAddress    = isPickupPhase ? task.pickupAddress       : task.dropoffAddress;

  // Deadline badge
  const deadlineRaw = task.deliveryDeadline || task.milestones?.deliveryDeadline || task.deliverBy;
  const deadlineBadge = (() => {
    if (!deadlineRaw) return null;
    const minsLeft = (new Date(deadlineRaw) - Date.now()) / 60000;
    const timeStr  = new Date(deadlineRaw).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (minsLeft < 0)   return { text: `OVERDUE · ${timeStr}`,                   textColor: "#DC2626", bg: "#FEE2E2" };
    if (minsLeft < 30)  return { text: `${Math.round(minsLeft)}m · ${timeStr}`,  textColor: "#DC2626", bg: "#FEE2E2" };
    if (minsLeft < 120) return { text: `${Math.round(minsLeft)}m · ${timeStr}`,  textColor: "#B45309", bg: "#FFF7ED" };
    const h = Math.floor(minsLeft / 60), m = Math.round(minsLeft % 60);
    return { text: `${h}h ${m}m · ${timeStr}`, textColor: "#374151", bg: "#F1F5F9" };
  })();

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: "10px",
        border: isEmerg     ? "2px solid #EF4444"
          : isArrived       ? "2px solid #10B981"
          : isArrivedPU     ? "2px solid #F59E0B"
          :                   "1px solid #D1D5DB",
        background: isVault ? "#F8FAFC"
          : isEmerg         ? "#FEF2F2"
          : isArrived       ? "#F0FDF4"
          : isArrivedPU     ? "#FFFBEB"
          :                   "#fff",
        opacity:    isVault ? 0.72 : 1,
        boxShadow: isVault ? "none"
          : isEmerg     ? "0 0 0 3px rgba(239,68,68,0.10), 0 4px 16px rgba(239,68,68,0.10)"
          : isArrived   ? "0 0 0 3px rgba(16,185,129,0.12)"
          : isArrivedPU ? "0 0 0 3px rgba(245,158,11,0.12)"
          :               "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Top accent stripe — colour changes with task state */}
      <Box sx={{ height: 4, background: accentColor }} />

      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>

        {/* ── Row 1: status chips + deadline badge ── */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
          <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
            {isEmerg && (
              <Chip label="EMERGENCY" size="small"
                sx={{ background: "#EF4444", color: "#fff", fontWeight: 800, fontSize: 10 }} />
            )}
            {taskTypeLabel && (
              <Chip label={taskTypeLabel} size="small"
                sx={{ background: taskTypeBg, color: taskTypeColor, fontWeight: 800, fontSize: 10 }} />
            )}
            <Chip
              label={(task.status || "Assigned").replace(/🚨\s*/g, "")}
              size="small"
              sx={{ background: accentColor, color: "#fff", fontWeight: 700, fontSize: 10 }}
            />
          </Box>
          {deadlineBadge && !isVault && (
            <Box
              sx={{
                background: deadlineBadge.bg,
                borderRadius: "8px",
                px: 1.25, py: 0.5,
                flexShrink: 0, ml: 1,
              }}
            >
              <Typography sx={{ fontSize: 10, fontWeight: 800, color: deadlineBadge.textColor, whiteSpace: "nowrap" }}>
                {deadlineBadge.text}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Step progress — L1 operational command context ── */}
        {stepInfo && (
          <Box display="flex" alignItems="center" gap={0.75} mb={1}>
            <Box
              sx={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: isArrived ? "#065F46" : isEmerg ? "#B91C1C" : "#475569",
                background: isArrived ? "#D1FAE5" : isEmerg ? "#FEE2E2" : "#E2E8F0",
                borderRadius: "3px", px: 0.75, py: 0.25,
                whiteSpace: "nowrap",
              }}
            >
              {stepInfo.step}
            </Box>
            <Typography
              sx={{
                fontSize: 12, fontWeight: 700,
                color: isArrived ? "#059669" : isEmerg ? "#B91C1C" : "#374151",
                letterSpacing: "0.01em",
              }}
            >
              {stepInfo.context}
            </Typography>
          </Box>
        )}

        {/* ── Mission title — route command line (L1) ── */}
        <Typography
          sx={{
            fontSize: pickupFacility ? 16 : 19,
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: pickupFacility ? "-0.005em" : "-0.01em",
            color: isEmerg ? "#B91C1C" : isArrived ? "#065F46" : "#111827",
            mb: 0.5,
          }}
        >
          {missionTitle}
        </Typography>

        {/* ── L2 reference row: case + tray count ── */}
        {isCaseSync && (task.caseNumber || task.linkedCaseId || task.trayCount > 0) && (
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              borderTop: "1px solid", borderColor: isEmerg ? "rgba(239,68,68,0.15)" : "#E2E8F0",
              pt: 1, mt: 0.5, mb: 1,
            }}
          >
            {task.trayCount > 0 && (
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {task.trayCount} {task.trayCount !== 1 ? "TRAYS" : "TRAY"}
              </Typography>
            )}
            {task.trayCount > 0 && (task.caseNumber || task.linkedCaseId) && (
              <Box sx={{ width: 1, height: 12, background: "#D1D5DB" }} />
            )}
            {(task.caseNumber || task.linkedCaseId) && (
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6366F1", letterSpacing: "0.04em" }}>
                CASE {task.caseNumber || String(task.linkedCaseId || "").substring(0, 8).toUpperCase()}
              </Typography>
            )}
            {task.surgeon && (
              <>
                <Box sx={{ width: 1, height: 12, background: "#D1D5DB" }} />
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#6B7280", letterSpacing: "0.03em" }}>
                  DR. {task.surgeon.toUpperCase()}
                </Typography>
              </>
            )}
            {/* Procedure summary — populated when task is linked to a case with procedure data */}
            {parseProcedureSummary(task.notes) && (
              <>
                <Box sx={{ width: 1, height: 12, background: "#D1D5DB" }} />
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#0369A1", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
                  {parseProcedureSummary(task.notes)}
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* ── Next Stop box — the operative focus of the card ── */}
        {!isVault && nextStopLabel && nextAddress && (
          <Box
            sx={{
              mt: isCaseSync ? 0 : 1,
              mb: 1.5,
              background: isArrived ? "#ECFDF5" : "#F8FAFC",
              border: "1px solid",
              borderColor: isArrived ? "#86EFAC" : "#E2E8F0",
              borderRadius: "6px",
              p: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box flex={1} minWidth={0}>
              <Typography
                sx={{
                  fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: isArrived ? "#059669" : "#6B7280",
                  mb: 0.25,
                }}
              >
                {isArrived ? "Arrived — Ready for Delivery" : `Next Stop · ${nextStopLabel}`}
              </Typography>
              {nextFacility && (
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: isArrived ? "#065F46" : "#111827", lineHeight: 1.3 }}>
                  {nextFacility}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: 12, color: "#374151", lineHeight: 1.4,
                  overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                }}
              >
                {nextAddress}
              </Typography>
            </Box>
            {/* Navigate link inline with the stop — not competing with the action button */}
            {!isArrived && (
              <Box
                component="a"
                href={nextStopMapsUrl(nextAddress)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flexShrink: 0,
                  background: "#3B82F6",
                  color: "#fff",
                  borderRadius: "5px",
                  px: 1.5, py: 0.75,
                  fontSize: 11, fontWeight: 700,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  lineHeight: 1.4,
                  "&:hover": { background: "#2563EB" },
                }}
              >
                Navigate
              </Box>
            )}
          </Box>
        )}

        {/* ── AxisSync SPD / Handoff Context — delivery tasks only ──────────────────
             Surfaces where to bring the load and any special handling requirements.
             Renders only when the task is AxisSync-linked, is in the delivery phase,
             and at least one piece of execution context is present.
             Native tasks are unaffected. No architecture labels shown to the driver. */}
        {isCaseSync && (task.taskType === "delivery" || isDropoffPhase) && !isVault && (() => {
          const hasTrayDest   = task.trayCount > 0 && task.trayDestination;
          const hasTemp       = task.temperatureControl && task.temperatureControl !== "ambient";
          const hasAttachment = !!task.attachmentUrl;
          if (!hasTrayDest && !hasTemp && !hasAttachment) return null;
          return (
            <Box
              sx={{
                mb: 1.5, p: 1.25,
                background: "#F0FDF4",
                border: "1px solid #BBF7D0",
                borderLeft: "3px solid #10B981",
                borderRadius: "6px",
              }}
            >
              <Typography
                sx={{
                  fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                  textTransform: "uppercase", color: "#065F46", mb: 0.75,
                }}
              >
                SPD / Handoff
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {hasTrayDest && (
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#111827" }}>
                    {task.trayCount} {task.trayCount === 1 ? "tray" : "trays"}{" "}
                    <Box component="span" sx={{ color: "#059669" }}>→</Box>{" "}
                    {task.trayDestination}
                  </Typography>
                )}
                {task.implantDestination && task.implantDestination !== task.trayDestination && (
                  <Typography sx={{ fontSize: 11, color: "#374151" }}>
                    Implants → {task.implantDestination}
                  </Typography>
                )}
                {hasTemp && (
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#1D4ED8" }}>
                    🌡 {String(task.temperatureControl).charAt(0).toUpperCase() +
                        String(task.temperatureControl).slice(1)} handling required
                  </Typography>
                )}
                {hasAttachment && (
                  <Typography
                    component="a"
                    href={task.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: 11, color: "#4338CA", fontWeight: 600,
                      textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 0.5,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    📎 PO / documentation attached
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })()}

        {/* ── GPS arrival monitoring notice — only when "picked up" with stored coords ── */}
        {isPickedUp && !isVault && hasDropoffCoords && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.25,
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderLeft: "3px solid #3B82F6",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                textTransform: "uppercase", color: "#1D4ED8", mb: 0.25,
              }}
            >
              GPS ARRIVAL MONITORING ACTIVE
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>
              Delivery will unlock automatically when you reach the delivery address.
            </Typography>
          </Box>
        )}

        {/* ── POD requirement ── */}
        {needsPOD && !isVault && (
          <Box display="inline-flex" alignItems="center" mb={1.5}>
            <Typography
              sx={{
                fontSize: 10, fontWeight: 800,
                color: "#854D0E", background: "#FEF9C3",
                border: "1px solid #FDE047",
                borderRadius: "6px", px: 1, py: 0.25,
              }}
            >
              POD Required: {POD_LABEL[req] ?? req}
            </Typography>
          </Box>
        )}

        {/* ── Notes — L3 reference (muted, labelled) ──────────────────────────────
             For AxisSync auto-generated tasks the notes field contains the
             structured base_info block ("Case #: ... | Surgeon: ... | Trays: …")
             which is already parsed and shown in the L2 reference row above.
             Suppress it here to avoid showing duplicate information to the driver.
             Custom dispatcher notes on AxisSync tasks (non-base_info format) and
             all native task notes are shown normally. */}
        {task.notes && !(
          isCaseSync && (
            String(task.notes).startsWith("Case #:") ||
            String(task.notes).startsWith("STAT / TRAUMA:")
          )
        ) && (
          <Typography
            sx={{
              fontSize: 10, color: "#9CA3AF", display: "block",
              mb: 1.25, letterSpacing: "0.01em",
              borderTop: "1px solid #F3F4F6", pt: 0.75,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700, color: "#6B7280", textTransform: "uppercase", fontSize: 9, letterSpacing: "0.07em", mr: 0.5 }}>
              NOTE —
            </Box>
            {task.notes}
          </Typography>
        )}

        {/* ── Inline blocked-pickup notice — shown after a failed pickup attempt ── */}
        {isEnRoute && !isVault && blockedPickupReason && (
          <Box
            sx={{
              mb: 1.5, p: 1.25,
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderLeft: "3px solid #F59E0B",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                textTransform: "uppercase", color: "#92400E", mb: 0.25,
              }}
            >
              PICKUP BLOCKED — MOVE TO PICKUP LOCATION
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5, mb: 0.5 }}>
              {blockedPickupReason}
            </Typography>
            <Typography sx={{ fontSize: 10, color: "#6B7280" }}>
              Once at the correct location, tap the button below to retry.
            </Typography>
            <Box mt={1}>
              {pickupOverrideApproved ? (
                <Chip size="small" color="success" label="DISPATCH OVERRIDE APPROVED" />
              ) : pickupOverrideRequested ? (
                <Chip size="small" color="warning" label="OVERRIDE REQUESTED — AWAITING DISPATCH" />
              ) : (
                <MDButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => onRequestOverride(task, "pickup")}
                  sx={{ fontWeight: 700, borderRadius: "6px" }}
                >
                  Request Dispatch Override
                </MDButton>
              )}
            </Box>
          </Box>
        )}

        {/* ── Inline blocked-arrival notice — shown after a failed arrival attempt ── */}
        {(isPickedUp || isEnRouteDL) && !isVault && blockedArrivalReason && (
          <Box
            sx={{
              mb: 1.5, p: 1.25,
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderLeft: "3px solid #F59E0B",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                textTransform: "uppercase", color: "#92400E", mb: 0.25,
              }}
            >
              {blockedArrivalReason.includes("coordinates are not set")
                ? "ARRIVAL BLOCKED — NO COORDINATES"
                : "ARRIVAL BLOCKED — MOVE TO DELIVERY LOCATION"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5, mb: 0.5 }}>
              {blockedArrivalReason}
            </Typography>
            <Typography sx={{ fontSize: 10, color: "#6B7280" }}>
              {blockedArrivalReason.includes("coordinates are not set")
                ? "Dispatch must set delivery coordinates before arrival can be confirmed."
                : "Once at the correct location, tap the button below to retry."}
            </Typography>
            <Box mt={1}>
              {arrivalOverrideApproved ? (
                <Chip size="small" color="success" label="DISPATCH OVERRIDE APPROVED" />
              ) : arrivalOverrideRequested ? (
                <Chip size="small" color="warning" label="OVERRIDE REQUESTED — AWAITING DISPATCH" />
              ) : (
                <MDButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => onRequestOverride(task, "arrival")}
                  sx={{ fontWeight: 700, borderRadius: "6px" }}
                >
                  Request Dispatch Override
                </MDButton>
              )}
            </Box>
          </Box>
        )}

        {/* ── Inline blocked-delivery notice — shown after a failed location attempt ── */}
        {/* Persists until the driver successfully completes delivery, so they always  */}
        {/* see the reason even after the auto-dismissing toast has disappeared.        */}
        {isArrived && !isVault && blockedReason && (
          <Box
            sx={{
              mb: 1.5, p: 1.25,
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderLeft: "3px solid #F59E0B",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                textTransform: "uppercase", color: "#92400E", mb: 0.25,
              }}
            >
              {blockedReason.includes("coordinates are not set")
                ? "DELIVERY BLOCKED — NO COORDINATES"
                : "DELIVERY BLOCKED — MOVE TO DELIVERY LOCATION"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5, mb: 0.5 }}>
              {blockedReason}
            </Typography>
            <Typography sx={{ fontSize: 10, color: "#6B7280" }}>
              {blockedReason.includes("coordinates are not set")
                ? "Dispatch must set delivery coordinates before this can complete."
                : "Once at the correct location, tap the button below to retry."}
            </Typography>
            <Box mt={1}>
              {deliveryOverrideApproved ? (
                <Chip size="small" color="success" label="DISPATCH OVERRIDE APPROVED" />
              ) : deliveryOverrideRequested ? (
                <Chip size="small" color="warning" label="OVERRIDE REQUESTED — AWAITING DISPATCH" />
              ) : (
                <MDButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => onRequestOverride(task, "delivery")}
                  sx={{ fontWeight: 700, borderRadius: "6px" }}
                >
                  Request Dispatch Override
                </MDButton>
              )}
            </Box>
          </Box>
        )}

        {/* ── Primary action — single large button, full width ── */}
        {isVault ? (
          <Box
            sx={{
              textAlign: "center", py: 0.75, borderRadius: "6px",
              background: "#F1F5F9", border: "1px solid #D1D5DB",
            }}
          >
            <Typography
              sx={{ fontSize: 10, fontWeight: 800, color: "#6B7280",
                textTransform: "uppercase", letterSpacing: "0.1em" }}
            >
              DELIVERED · ARCHIVED
            </Typography>
          </Box>
        ) : isEnRoute && !hasPickupCoords && !pickupOverrideApproved ? (
          // No stored pickup coordinates — pickup cannot be confirmed by the driver.
          // Dispatcher override is the only allowed completion path.
          <Box
            sx={{
              mt: 0.5, p: 1.25,
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderLeft: "3px solid #EF4444",
              borderRadius: "6px",
            }}
          >
            <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#B91C1C", mb: 0.25 }}>
              PICKUP BLOCKED — NO COORDINATES
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>
              Pickup coordinates are not set on this task.{"\n"}Contact dispatch to confirm pickup.
            </Typography>
            <Box mt={1}>
              {pickupOverrideRequested ? (
                <Chip size="small" color="warning" label="OVERRIDE REQUESTED — AWAITING DISPATCH" />
              ) : (
                <MDButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => onRequestOverride(task, "pickup")}
                  sx={{ fontWeight: 700, borderRadius: "6px" }}
                >
                  Request Dispatch Override
                </MDButton>
              )}
            </Box>
          </Box>
        ) : (isPickedUp || isEnRouteDL) && !hasDropoffCoords && !arrivalOverrideApproved ? (
          // No stored coordinates — arrival and delivery cannot be confirmed by the driver.
          // Dispatcher override is the only allowed completion path.
          <Box
            sx={{
              mt: 0.5, p: 1.25,
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderLeft: "3px solid #EF4444",
              borderRadius: "6px",
            }}
          >
            <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#B91C1C", mb: 0.25 }}>
              ARRIVAL BLOCKED — NO COORDINATES
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>
              Delivery coordinates are not set on this task.{"\n"}Contact dispatch to confirm arrival.
            </Typography>
            <Box mt={1}>
              {arrivalOverrideRequested ? (
                <Chip size="small" color="warning" label="OVERRIDE REQUESTED — AWAITING DISPATCH" />
              ) : (
                <MDButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => onRequestOverride(task, "arrival")}
                  sx={{ fontWeight: 700, borderRadius: "6px" }}
                >
                  Request Dispatch Override
                </MDButton>
              )}
            </Box>
          </Box>
        ) : cycleEntry ? (
          <MDButton
            variant="gradient"
            color={cycleEntry.color}
            fullWidth
            size="large"
            disabled={isUpdating}
            onClick={handleCycleClick}
            sx={{
              fontWeight: 800,
              fontSize: 15,
              borderRadius: "8px",
              py: 1.5,
              mt: 0.5,
              letterSpacing: "0.02em",
              boxShadow: isArrived
                ? "0 4px 20px rgba(16,185,129,0.40)"
                : isEmerg
                ? "0 4px 20px rgba(239,68,68,0.30)"
                : "0 4px 14px rgba(59,130,246,0.30)",
            }}
          >
            {isUpdating
              ? <CircularProgress size={22} color="inherit" />
              : cycleEntry.label
            }
          </MDButton>
        ) : (
          // Fallback when status is outside the cycle (e.g. unknown value)
          <Box
            component="a"
            href={mapsUrl(task)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "block", textAlign: "center",
              background: "#F1F5F9", borderRadius: "6px",
              py: 1, mt: 0.5, fontSize: 11, fontWeight: 700,
              color: "#374151", textDecoration: "none",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}
          >
            Open in Maps
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DriverApp({ readOnly, supportViewData }) {
  const navigate = useNavigate();
  const session    = getCurrentUser();
  const driverName = session?.name ?? "Driver";

  // Driver Support View mode
  // When readOnly is true, this is a read-only mirror of a specific driver's state.
  // We use supportViewData (pre-fetched by the container) instead of API calls.
  // NOTE: only variables that don't depend on state declared further below
  // (tasks/phase/activeTasks/vaultTasks) may be computed here — those derived
  // values are computed further down, right after their dependencies exist.
  const isSupportView = readOnly === true;
  const effectiveDriverName = isSupportView
    ? (supportViewData?.driver?.name ?? "Driver")
    : driverName;
  const effectiveShift = isSupportView ? supportViewData?.shift : null;
  const effectiveOdometer = isSupportView ? supportViewData?.odometer : null;
  const effectiveLocation = isSupportView ? supportViewData?.location : null;

  // ── Phase ────────────────────────────────────────────────────────────────────
  const [phase,      setPhase]      = useState("pre-shift");
  const [shiftId,    setShiftId]    = useState(null);
  const [startOdom,  setStartOdom]  = useState("");
  const [endOdom,    setEndOdom]    = useState("");
  const [shiftStart, setShiftStart] = useState(null);

  // 1.0.2 — mandatory odometer photos (anti-fraud accountability control).
  const [startOdomPhoto, setStartOdomPhoto] = useState(null);
  const [endOdomPhoto,   setEndOdomPhoto]   = useState(null);
  const startOdomPhotoInputRef = useRef(null);
  const endOdomPhotoInputRef   = useRef(null);

  // ── Tasks ────────────────────────────────────────────────────────────────────
  const [tasks,        setTasks]        = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [updatingId,   setUpdatingId]   = useState(null);

  // ── Last sync ────────────────────────────────────────────────────────────────
  const [lastSynced, setLastSynced] = useState(null);

  // ── Shift busy / errors ───────────────────────────────────────────────────────
  const [busyShift,  setBusyShift]  = useState(false);
  const [shiftError, setShiftError] = useState("");
  const [taskError,  setTaskError]  = useState("");
  const [toast,      setToast]      = useState({ open: false, msg: "", color: "success" });

  // ── POD modal ─────────────────────────────────────────────────────────────────
  const [podTask,        setPodTask]        = useState(null); // task requiring delivery POD
  const [pickupPodTask,  setPickupPodTask]  = useState(null); // task requiring pickup proof
  const [overrideDialog, setOverrideDialog] = useState({ task: null, type: "" });

  // ── Blocked-delivery inline notice (cleared on retry success) ────────────────
  // Tracks the most recent location-blocked attempt so the card can show an
  // inline retry notice instead of relying solely on the auto-dismissing toast.
  const [blockedDelivery, setBlockedDelivery] = useState({ taskId: null, reason: "" });
  const [blockedArrival,  setBlockedArrival]  = useState({ taskId: null, reason: "" });
  const [blockedPickup,   setBlockedPickup]   = useState({ taskId: null, reason: "" });

  // ── Refs ──────────────────────────────────────────────────────────────────────
  const pollRef          = useRef(null);
  const watchRef         = useRef(null); // navigator.geolocation.watchPosition ID
  const lastLocUpdateRef = useRef(0);    // timestamp of last PUT /driver/location call

  const publishCurrentLocation = useCallback(async () => {
    if (isSupportView) return;
    if (!navigator?.geolocation || phase !== "on-shift") return;
    const coords = await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 }
      );
    });
    if (!coords) return;
    if ((coords.lat === 0 && coords.lng === 0) || isNaN(coords.lat) || isNaN(coords.lng)) return;
    lastLocUpdateRef.current = Date.now();
    updateMyLocation(coords.lat, coords.lng).catch(() => {});
  }, [phase]);

  // ── Fetch tasks ───────────────────────────────────────────────────────────────
  const fetchTasks = useCallback(async (quiet = false) => {
    if (isSupportView) return;
    if (!quiet) setLoadingTasks(true);
    setTaskError("");
    try {
      const res  = await getMyTasks();
      const data = await res.json().catch(() => null);
      if (res.ok && Array.isArray(data)) {
        const mine = data.filter((t) => {
          const assigned = (t.assignedDriver || t.driver || "").toLowerCase();
          return assigned === driverName.toLowerCase() || assigned === "";
        });
        setTasks(mine);
        // Clear blockedArrival when task is no longer in a delivery-leg status
        if (!mine.some((t) => {
          const sk = normalizeTaskStatus(t.status);
          return t.id === blockedArrival.taskId && (
            sk === normalizeTaskStatus(TASK_STATUS.PICKED_UP) ||
            sk === normalizeTaskStatus(TASK_STATUS.EN_ROUTE_DL)
          );
        })) {
          setBlockedArrival({ taskId: null, reason: "" });
        }
        if (mine.some((t) => t.id === blockedArrival.taskId && isOverrideApproved(t, "arrival"))) {
          setBlockedArrival({ taskId: null, reason: "" });
        }
        if (!mine.some((t) => t.id === blockedDelivery.taskId && normalizeTaskStatus(t.status) === normalizeTaskStatus(TASK_STATUS.ARRIVED))) {
          setBlockedDelivery({ taskId: null, reason: "" });
        }
        if (mine.some((t) => t.id === blockedDelivery.taskId && isOverrideApproved(t, "delivery"))) {
          setBlockedDelivery({ taskId: null, reason: "" });
        }
        // Clear blockedPickup when task is no longer in EN_ROUTE (pickup attempt failed)
        if (!mine.some((t) => {
          const sk = normalizeTaskStatus(t.status);
          return t.id === blockedPickup.taskId && (
            sk === normalizeTaskStatus(TASK_STATUS.EN_ROUTE) ||
            sk === normalizeTaskStatus(TASK_STATUS.ARRIVED_PU)
          );
        })) {
          setBlockedPickup({ taskId: null, reason: "" });
        }
        if (mine.some((t) => t.id === blockedPickup.taskId && isOverrideApproved(t, "pickup"))) {
          setBlockedPickup({ taskId: null, reason: "" });
        }
        setLastSynced(new Date());
      } else if (!quiet) {
        setTaskError(data?.detail ?? "Could not load tasks.");
      }
    } catch {
      if (!quiet) setTaskError("Server unreachable. Retrying every 30 s.");
    } finally {
      if (!quiet) setLoadingTasks(false);
    }
  }, [driverName, blockedArrival.taskId, blockedDelivery.taskId]);

  // Poll + start geofencing when shift goes active
  // Disabled in support view
  useEffect(() => {
    if (isSupportView) {
      clearInterval(pollRef.current);
      if (watchRef.current != null && navigator?.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
      return;
    }
    if (phase !== "on-shift") {
      clearInterval(pollRef.current);
      if (watchRef.current != null && navigator?.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
      return;
    }

    // Initial fetch + immediate location publish so dispatch sees the driver
    // without waiting for the first watchPosition cycle.
    fetchTasks();
    publishCurrentLocation();

    // 30-second background poll
    pollRef.current = setInterval(() => fetchTasks(true), 30_000);

    // GPS: geofencing + periodic location publishing
    const LOC_PUBLISH_INTERVAL_MS = 30_000; // same cadence as the task poll

    if (navigator?.geolocation) {
      watchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          const accuracy = pos.coords.accuracy ?? null;
          const reading  = { lat, lng, accuracy };
          // Auto-arrival uses the same accuracy-aware fence as the manual
          // buttons and the backend, but never fires on a "weak" (>250 m) fix.
          const insideStop = (task, prefix) => {
            const target = stopCoords(task, prefix);
            if (!target || isWeakAccuracy(accuracy)) return false;
            return checkGeofence(reading, target).isInside;
          };

          // ── Two-leg geofence: auto-trigger state transitions ───────────────
          // Leg 1: EN_ROUTE → Arrived (PU) at pickupLat/pickupLng
          // Leg 2: EN_ROUTE_DL → Arrived at dropoffLat/dropoffLng
          // Backward-compat: PICKED_UP → Arrived at dropoffLat/dropoffLng (legacy tasks)
          setTasks((prev) => {
            let anyChanged = false;
            const next = prev.map((t) => {
              const sk = normalizeTaskStatus(t.status);

              // Leg 1 — arrive at pickup
              if (sk === normalizeTaskStatus(TASK_STATUS.EN_ROUTE)) {
                if (insideStop(t, "pickup")) {
                  updateTaskStatus(t.id, TASK_STATUS.ARRIVED_PU, reading).catch(() => {});
                  setBlockedPickup((bp) => bp.taskId === t.id ? { taskId: null, reason: "" } : bp);
                  anyChanged = true;
                  return { ...t, status: TASK_STATUS.ARRIVED_PU };
                }
                return t;
              }

              // Leg 2 — arrive at delivery (new En Route (DL) status)
              if (sk === normalizeTaskStatus(TASK_STATUS.EN_ROUTE_DL)) {
                if (insideStop(t, "dropoff")) {
                  updateTaskStatus(t.id, "Arrived", reading).catch(() => {});
                  setBlockedArrival((ba) => ba.taskId === t.id ? { taskId: null, reason: "" } : ba);
                  anyChanged = true;
                  return { ...t, status: "Arrived" };
                }
                return t;
              }

              // Backward-compat: legacy tasks in PICKED_UP still auto-trigger Arrived
              if (sk === normalizeTaskStatus(TASK_STATUS.PICKED_UP)) {
                if (insideStop(t, "dropoff")) {
                  updateTaskStatus(t.id, "Arrived", reading).catch(() => {});
                  setBlockedArrival((ba) => ba.taskId === t.id ? { taskId: null, reason: "" } : ba);
                  anyChanged = true;
                  return { ...t, status: "Arrived" };
                }
                return t;
              }

              return t;
            });
            return anyChanged ? next : prev;
          });

          // ── Publish driver position to backend (throttled) ──────────────
          // Live Map reads last_lat/last_lng from GET /api/drivers.
          // Only send if coordinates look valid (not 0,0, not NaN).
          const now = Date.now();
          if (
            now - lastLocUpdateRef.current >= LOC_PUBLISH_INTERVAL_MS &&
            !(lat === 0 && lng === 0) &&
            !isNaN(lat) && !isNaN(lng)
          ) {
            lastLocUpdateRef.current = now;
            updateMyLocation(lat, lng).catch(() => {}); // best-effort, never throws to UI
          }
        },
        null,
        { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 }
      );
    }

    return () => {
      clearInterval(pollRef.current);
      if (watchRef.current != null && navigator?.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
    };
  }, [phase, fetchTasks, publishCurrentLocation]);

  // ── Shift start ───────────────────────────────────────────────────────────────
  const handleStartOdomPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setStartOdomPhoto(await resizeImageBase64(file));
    } catch {
      setShiftError("Could not process the odometer photo. Please try again.");
    }
  };

  const handleStartShift = async () => {
    if (isSupportView) return;
    const odom = parseInt(startOdom, 10);
    if (!startOdom || isNaN(odom) || odom < 0) {
      setShiftError("Please enter a valid starting odometer reading.");
      return;
    }
    if (!startOdomPhoto) {
      setShiftError("A starting odometer photo is required before the shift can begin.");
      return;
    }
    setShiftError("");
    setBusyShift(true);
    try {
      const res  = await startDriverShift(odom, startOdomPhoto);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setShiftId(data.id ?? null);
        setShiftStart(data.startTime ?? new Date().toISOString());
        setStartOdomPhoto(null);
        setPhase("on-shift");
      } else {
        setShiftError(data?.detail ?? `Failed to start shift (HTTP ${res.status}).`);
      }
    } catch {
      setShiftError("Cannot reach the server. Check your connection.");
    } finally {
      setBusyShift(false);
    }
  };

  // ── Standard status action (non-delivery) ─────────────────────────────────────
  const handleStatusAction = async (task, nextStatus) => {
    if (isSupportView) return;
    const taskId = task.id;

    // ── Two-leg: Arrived (PU) — GPS validation at pickup address ──────────────
    if (nextStatus === TASK_STATUS.ARRIVED_PU) {
      setUpdatingId(taskId);
      const check = isOverrideApproved(task, "pickup")
        ? { allowed: true, coords: null, reason: "dispatcher-override-approved" }
        : await validatePickupLocation(task);
      if (!check.allowed) {
        showToast("Move to pickup location — then retry", "error");
        setBlockedPickup({ taskId, reason: check.reason });
        setUpdatingId(null);
        return;
      }
      try {
        const res  = await updateTaskStatus(taskId, TASK_STATUS.ARRIVED_PU, check.coords ?? {});
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setBlockedPickup({ taskId: null, reason: "" });
          setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: TASK_STATUS.ARRIVED_PU } : t));
          showToast(ACTION_MESSAGES[TASK_STATUS.ARRIVED_PU]);
        } else {
          showToast(data?.detail ?? `Update failed (HTTP ${res.status}).`, "error");
        }
      } catch {
        showToast("Cannot reach the server.", "error");
      } finally {
        setUpdatingId(null);
      }
      return;
    }

    // ── Two-leg: Picked Up — from Arrived (PU) (no location re-check needed) ─
    // The PickupProofModal handles this transition when proof is required.
    // This handler covers the no-proof path from Arrived (PU).
    if (nextStatus === "Picked Up") {
      const currentNorm = normalizeTaskStatus(task.status);
      const fromArrivedPU = currentNorm === normalizeTaskStatus(TASK_STATUS.ARRIVED_PU);

      setUpdatingId(taskId);
      const check = fromArrivedPU
        // Already geo-confirmed at pickup via Arrived (PU) — no re-validation
        ? { allowed: true, coords: null, reason: "from-arrived-pu" }
        : isOverrideApproved(task, "pickup")
          ? { allowed: true, coords: null, reason: "dispatcher-override-approved" }
          : await validatePickupLocation(task);

      if (!check.allowed) {
        showToast("Move to pickup location — then retry", "error");
        setBlockedPickup({ taskId, reason: check.reason });
        setUpdatingId(null);
        return;
      }
      try {
        const res  = await updateTaskStatus(taskId, "Picked Up", check.coords ?? {});
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setBlockedPickup({ taskId: null, reason: "" });
          setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: "Picked Up" } : t));
          showToast(ACTION_MESSAGES[TASK_STATUS.PICKED_UP]);
        } else {
          showToast(data?.detail ?? `Update failed (HTTP ${res.status}).`, "error");
        }
      } catch {
        showToast("Cannot reach the server.", "error");
      } finally {
        setUpdatingId(null);
      }
      return;
    }

    // ── Two-leg: En Route (DL) — no location enforcement, just advance ────────
    if (nextStatus === TASK_STATUS.EN_ROUTE_DL) {
      setUpdatingId(taskId);
      try {
        let coords = null;
        if (navigator?.geolocation) {
          coords = await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null }),
              ()  => resolve(null),
              { enableHighAccuracy: true, timeout: 5_000, maximumAge: 0 }
            );
          });
        }
        const res  = await updateTaskStatus(taskId, TASK_STATUS.EN_ROUTE_DL, coords);
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: TASK_STATUS.EN_ROUTE_DL } : t));
          showToast(ACTION_MESSAGES[TASK_STATUS.EN_ROUTE_DL]);
        } else {
          showToast(data?.detail ?? `Update failed (HTTP ${res.status}).`, "error");
        }
      } catch {
        showToast("Cannot reach the server.", "error");
      } finally {
        setUpdatingId(null);
      }
      return;
    }

    // ── ENFORCEMENT: Arrived — GPS validation at delivery address ──────────────
    // Fires from the manual "Confirm Arrival" button (tasks without stored coords
    // or tasks in EN_ROUTE_DL that did not auto-geofence).
    if (nextStatus === "Arrived") {
      setUpdatingId(taskId);
      const check = isOverrideApproved(task, "arrival")
        ? { allowed: true, coords: null, reason: "dispatcher-override-approved" }
        : await validateArrivalLocation(task);
      if (!check.allowed) {
        showToast("Move to delivery location — then retry arrival", "error");
        setBlockedArrival({ taskId, reason: check.reason });
        setUpdatingId(null);
        return;
      }
      try {
        const res  = await updateTaskStatus(taskId, "Arrived", check.coords ?? {});
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setBlockedArrival({ taskId: null, reason: "" });
          setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: "Arrived" } : t));
          showToast(ACTION_MESSAGES[TASK_STATUS.ARRIVED] ?? "Arrived at delivery location — confirmed.");
        } else {
          showToast(data?.detail ?? `Update failed (HTTP ${res.status}).`, "error");
        }
      } catch {
        showToast("Cannot reach the server.", "error");
      } finally {
        setUpdatingId(null);
      }
      return;
    }

    // ── ENFORCEMENT: Delivered — POD gate + location gate ──────────────────────
    if (nextStatus === "Delivered") {
      const podReq = (task.podRequirement ?? "none").toLowerCase().replace(/-/g, "_");
      if (podReq !== "none") {
        setPodTask(task);
        return;
      }
      setUpdatingId(taskId);
      const check = isOverrideApproved(task, "delivery")
        ? { allowed: true, coords: null, reason: "dispatcher-override-approved" }
        : await validateDeliveryLocation(task);
      if (!check.allowed) {
        showToast("Move to delivery location — then retry", "error");
        setBlockedDelivery({ taskId, reason: check.reason });
        setUpdatingId(null);
        return;
      }
      try {
        const res  = await updateTaskStatus(taskId, "Delivered", check.coords ?? {});
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setBlockedDelivery({ taskId: null, reason: "" });
          setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: "Delivered" } : t));
          showToast(ACTION_MESSAGES[TASK_STATUS.DELIVERED] ?? "Delivered — confirmed.");
        } else {
          showToast(data?.detail ?? `Update failed (HTTP ${res.status}).`, "error");
        }
      } catch {
        showToast("Cannot reach the server.", "error");
      } finally {
        setUpdatingId(null);
      }
      return;
    }

    // ── Standard status transitions (En Route, etc.) ─────────────────────────
    setUpdatingId(taskId);
    try {
      let coords = null;
      if (navigator?.geolocation) {
        coords = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null }),
            ()  => resolve(null),
            { enableHighAccuracy: true, timeout: 5_000, maximumAge: 0 }
          );
        });
      }
      const res  = await updateTaskStatus(taskId, nextStatus, coords);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: nextStatus } : t));
        showToast(ACTION_MESSAGES[nextStatus] ?? `Status updated: ${nextStatus}`);
      } else {
        showToast(data?.detail ?? `Update failed (HTTP ${res.status}).`, "error");
      }
    } catch {
      showToast("Cannot reach the server.", "error");
    } finally {
      setUpdatingId(null);
    }
  };

  // ── Pickup proof callback (called by PickupProofModal on success) ─────────────
  const handlePickupPODConfirmed = (taskId) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: "Picked Up" } : t))
    );
    showToast("Pickup confirmed — proof saved.");
  };

  // ── POD delivery callback (called by PODModal on success) ─────────────────────
  const handlePODDelivered = (taskId) => {
    setBlockedDelivery({ taskId: null, reason: "" }); // clear any prior block on success
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: "Delivered" } : t))
    );
    showToast("Delivered — proof saved.");
  };

  const handleOverrideRequested = (updatedTask, overrideType) => {
    setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t)));
    showToast(
      `${overrideType === "arrival" ? "Arrival" : "Delivery"} override requested — waiting for dispatch`
    );
  };

  // ── Shift end ─────────────────────────────────────────────────────────────────
  const handleEndOdomPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setEndOdomPhoto(await resizeImageBase64(file));
    } catch {
      setShiftError("Could not process the odometer photo. Please try again.");
    }
  };

  const handleEndShift = async () => {
    if (isSupportView) return;
    const odom = parseInt(endOdom, 10);
    if (!endOdom || isNaN(odom) || odom < 0) {
      setShiftError("Please enter a valid ending odometer reading.");
      return;
    }
    if (!endOdomPhoto) {
      setShiftError("An ending odometer photo is required before the shift can be closed.");
      return;
    }
    setShiftError("");
    setBusyShift(true);
    try {
      const res  = await endDriverShift(shiftId, odom, endOdomPhoto);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const miles = data.totalMiles != null
          ? `${parseFloat(data.totalMiles).toFixed(1)} miles logged.`
          : "";
        showToast(`Shift complete! ${miles}`);
        setTimeout(() => {
          setPhase("pre-shift");
          setShiftId(null);
          setTasks([]);
          setStartOdom("");
          setEndOdom("");
          setEndOdomPhoto(null);
          setShiftStart(null);
        }, 1_500);
      } else {
        setShiftError(data?.detail ?? `Failed to end shift (HTTP ${res.status}).`);
      }
    } catch {
      setShiftError("Cannot reach the server. Check your connection.");
    } finally {
      setBusyShift(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const showToast = (msg, color = "success") => {
    setToast({ open: true, msg, color });
    // Auto-dismiss: prevents the toast from covering the action button on mobile.
    // Error toasts linger longer so the driver has time to read the reason.
    setTimeout(
      () => setToast((prev) => (prev.msg === msg ? { ...prev, open: false } : prev)),
      color === "error" ? 6_000 : 3_000
    );
  };
  const handleLogout = () => { if (isSupportView) return; logoutUser(); navigate("/login", { replace: true }); };

  // ── Derived lists ─────────────────────────────────────────────────────────────
  const activeTasks = tasks.filter((t) => !isTerminalTaskStatus(t.status));
  const vaultTasks  = tasks.filter((t) =>  isTerminalTaskStatus(t.status));

  // Driver Support View — derived lists that depend on tasks/activeTasks/vaultTasks
  // (must come after the declarations above; see note near isSupportView).
  const effectiveTasks = isSupportView
    ? (supportViewData?.tasks ?? [])
    : tasks;
  const supportViewPhase = isSupportView ? "on-shift" : phase;
  const supportViewActiveTasks = isSupportView
    ? effectiveTasks.filter((t) => !isTerminalTaskStatus(t.status))
    : activeTasks;
  const supportViewVaultTasks = isSupportView
    ? effectiveTasks.filter((t) => isTerminalTaskStatus(t.status))
    : vaultTasks;

  // Live operational status derived from active task states
  const driverOpsStatus = (() => {
    const statuses = activeTasks.map((t) => (t.status || "").toLowerCase());
    if (statuses.includes(normalizeTaskStatus(TASK_STATUS.ARRIVED)))   return { label: "Arrived",    bg: "#10B981", textColor: "#fff" };
    if (statuses.includes(normalizeTaskStatus(TASK_STATUS.PICKED_UP))) return { label: "In Transit", bg: "#7C3AED", textColor: "#fff" };
    if (statuses.includes(normalizeTaskStatus(TASK_STATUS.EN_ROUTE)))  return { label: "En Route",   bg: "#F59E0B", textColor: "#fff" };
    if (statuses.length > 0)             return { label: "Assigned",   bg: "#3B82F6", textColor: "#fff" };
    return { label: "Available", bg: "#10B981", textColor: "#fff" };
  })();

  // Human-readable last-synced label
  const lastSyncedLabel = (() => {
    if (!lastSynced) return null;
    const secAgo = Math.round((Date.now() - lastSynced) / 1000);
    if (secAgo < 10)  return "just now";
    if (secAgo < 60)  return `${secAgo}s ago`;
    const minAgo = Math.round(secAgo / 60);
    return `${minAgo}m ago`;
  })();

  // ── Toast component ────────────────────────────────────────────────────────────
  const Toast = toast.open ? (
    <Box
      onClick={() => setToast((p) => ({ ...p, open: false }))}
      sx={{
        position: "fixed",
        bottom: phase === "on-shift" ? 80 : 24,
        left: "50%", transform: "translateX(-50%)",
        background: toast.color === "error" ? "#EF4444" : "#10B981",
        color: "#fff", borderRadius: 2, px: 3, py: 1.25,
        boxShadow: "0 4px 20px rgba(0,0,0,0.3)", cursor: "pointer",
        zIndex: 1200, whiteSpace: "nowrap",
      }}
    >
      <Typography variant="body2" fontWeight={700}>{toast.msg}</Typography>
    </Box>
  ) : null;

  // ════════════════════════════════════════════════════════════════════════════
  // PHASE: pre-shift
  // ════════════════════════════════════════════════════════════════════════════
  if (isSupportView ? false : phase === "pre-shift") {
    return (
      <Box sx={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: "linear-gradient(160deg, #0f172a 0%, #1e293b 100%)",
        px: 2, py: 4,
      }}>
        <Box sx={{ mb: 5, px: 3, py: 2.5, borderRadius: "16px", background: "#fff",
          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Logo sx={{ height: 36 }} />
        </Box>

        <Card sx={{ width: "100%", maxWidth: 420, borderRadius: 4,
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={3}>
              <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {greeting()}
              </Typography>
              <Typography variant="h5" fontWeight={800}
                sx={{ mt: 0.5, color: "#111827", letterSpacing: "-0.02em" }}>
                {driverName}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            {shiftError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShiftError("")}>
                {shiftError}
              </Alert>
            )}
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700,
              color: "#374151", letterSpacing: "0.06em", textTransform: "uppercase", mb: 1 }}>
              Starting Odometer (miles)
            </Typography>
            <MDInput
              type="number"
              placeholder="e.g. 42,500"
              value={startOdom}
              onChange={(e) => setStartOdom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleStartShift(); }}
              fullWidth
              sx={{ mb: 3 }}
              inputProps={{ min: 0, step: 1,
                style: { fontSize: 24, fontWeight: 700, textAlign: "center" } }}
              disabled={busyShift}
            />

            <Typography variant="caption" sx={{ display: "block", fontWeight: 700,
              color: "#374151", letterSpacing: "0.06em", textTransform: "uppercase", mb: 1 }}>
              Starting Odometer Photo
            </Typography>
            <input
              ref={startOdomPhotoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handleStartOdomPhoto}
            />
            {startOdomPhoto ? (
              <Box mb={2}>
                <Box
                  component="img"
                  src={startOdomPhoto}
                  alt="Starting odometer"
                  sx={{ width: "100%", maxHeight: 180, objectFit: "cover",
                    borderRadius: "10px", border: "1.5px solid #86EFAC" }}
                />
                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => { setStartOdomPhoto(null); if (startOdomPhotoInputRef.current) startOdomPhotoInputRef.current.value = ""; }}
                  >
                    Retake
                  </Typography>
                </Box>
              </Box>
            ) : (
              <MDButton
                variant="outlined" color="dark" fullWidth
                onClick={() => startOdomPhotoInputRef.current?.click()}
                disabled={busyShift}
                sx={{ mb: 2, py: 1.25, fontWeight: 700, borderRadius: 2 }}
              >
                📷 Photograph Odometer
              </MDButton>
            )}

            <MDButton
              variant="gradient" color="success" fullWidth size="large"
              onClick={handleStartShift} disabled={busyShift}
              sx={{ py: 1.75, fontSize: 15, fontWeight: 800, borderRadius: 2 }}
            >
              {busyShift ? <CircularProgress size={22} color="inherit" /> : "Start Shift"}
            </MDButton>
          </CardContent>
        </Card>

        <Typography variant="caption" onClick={handleLogout}
          sx={{ mt: 3, color: "rgba(255,255,255,0.45)", cursor: "pointer",
            textDecoration: "underline" }}>
          Not {driverName.split(" ")[0]}? Log out
        </Typography>
        {Toast}
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PHASE: ending
  // ════════════════════════════════════════════════════════════════════════════
  if (isSupportView ? false : phase === "ending") {
    const startedAt = shiftStart
      ? new Date(shiftStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "—";

    return (
      <Box sx={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: "linear-gradient(160deg, #0f172a 0%, #1e293b 100%)",
        px: 2, py: 4,
      }}>
        <Box sx={{ mb: 4, px: 3, py: 2.5, borderRadius: "16px", background: "#fff",
          boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Logo sx={{ height: 34 }} />
        </Box>
        <Card sx={{ width: "100%", maxWidth: 420, borderRadius: 4,
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}>
          <CardContent sx={{ p: 3.5 }}>
            <Box textAlign="center" mb={3}>
              <Typography variant="h5" fontWeight={800}>Shift Summary</Typography>
              <Typography variant="body2" color="secondary" mt={0.5}>{driverName}</Typography>
            </Box>
            <Box sx={{ background: "#f8fafc", borderRadius: "12px", p: 2, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="caption" color="secondary" fontWeight={700}>SHIFT STARTED</Typography>
                <Typography variant="caption" fontWeight={800}>{startedAt}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="caption" color="secondary" fontWeight={700}>STARTING ODOMETER</Typography>
                <Typography variant="caption" fontWeight={800}>
                  {parseInt(startOdom, 10).toLocaleString()} mi
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="secondary" fontWeight={700}>DELIVERIES</Typography>
                <Typography variant="caption" fontWeight={800}>
                  {vaultTasks.filter((t) => (t.status || "").toLowerCase() === "delivered").length} delivered
                </Typography>
              </Box>
            </Box>
            {shiftError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShiftError("")}>
                {shiftError}
              </Alert>
            )}
            <MDInput
              type="number"
              label="Ending Odometer (miles)"
              value={endOdom}
              onChange={(e) => setEndOdom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleEndShift(); }}
              fullWidth sx={{ mb: 3 }}
              inputProps={{ min: parseInt(startOdom, 10) || 0, step: 1,
                style: { fontSize: 20, fontWeight: 700, textAlign: "center" } }}
              disabled={busyShift}
            />

            <Typography variant="caption" sx={{ display: "block", fontWeight: 700,
              color: "#374151", letterSpacing: "0.06em", textTransform: "uppercase", mb: 1 }}>
              Ending Odometer Photo
            </Typography>
            <input
              ref={endOdomPhotoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handleEndOdomPhoto}
            />
            {endOdomPhoto ? (
              <Box mb={2}>
                <Box
                  component="img"
                  src={endOdomPhoto}
                  alt="Ending odometer"
                  sx={{ width: "100%", maxHeight: 180, objectFit: "cover",
                    borderRadius: "10px", border: "1.5px solid #86EFAC" }}
                />
                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => { setEndOdomPhoto(null); if (endOdomPhotoInputRef.current) endOdomPhotoInputRef.current.value = ""; }}
                  >
                    Retake
                  </Typography>
                </Box>
              </Box>
            ) : (
              <MDButton
                variant="outlined" color="dark" fullWidth
                onClick={() => endOdomPhotoInputRef.current?.click()}
                disabled={busyShift}
                sx={{ mb: 2, py: 1.25, fontWeight: 700, borderRadius: 2 }}
              >
                📷 Photograph Odometer
              </MDButton>
            )}

            <MDButton
              variant="gradient" color="success" fullWidth size="large"
              onClick={handleEndShift} disabled={busyShift}
              sx={{ py: 1.5, fontWeight: 800, borderRadius: "12px", mb: 1.5 }}
            >
              {busyShift ? <CircularProgress size={20} color="inherit" /> : "Submit & Clock Out"}
            </MDButton>
            <Box textAlign="center">
              <Typography variant="caption" color="secondary"
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => { setPhase("on-shift"); setShiftError(""); setEndOdom(""); setEndOdomPhoto(null); }}>
                ← Back to Active Shift
              </Typography>
            </Box>
          </CardContent>
        </Card>
        {Toast}
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PHASE: on-shift
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ maxWidth: 640, mx: "auto", px: 2, pt: 3, pb: "90px" }}>

      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Logo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(0,0,0,0.12)", mx: 0.5 }} />
            <Box>
              <Typography
                fontWeight={800}
                sx={{ fontSize: 16, letterSpacing: "-0.01em", lineHeight: 1.2 }}
              >
                {driverName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
              >
                {shiftStart
                  ? `Since ${new Date(shiftStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                  : "Active Shift"}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
            <Chip
              label={driverOpsStatus.label}
              size="small"
              sx={{
                background: driverOpsStatus.bg,
                color: driverOpsStatus.textColor,
                fontWeight: 800, fontSize: 11, letterSpacing: "0.06em",
                px: 0.5,
              }}
            />
            {lastSyncedLabel && (
              <Typography sx={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, whiteSpace: "nowrap" }}>
                synced {lastSyncedLabel}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Active-task count strip */}
        {(isSupportView ? supportViewActiveTasks : activeTasks).length > 0 && (
          <Box
            sx={{
              background: "#EFF6FF",
              borderRadius: "6px",
              border: "1px solid #BFDBFE",
              px: 2, py: 0.875,
              display: "flex", alignItems: "center", gap: 1.25,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#3B82F6", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#1D4ED8",
              textTransform: "uppercase", letterSpacing: "0.07em" }}>
              {activeTasks.length} ACTIVE TASK{activeTasks.length !== 1 ? "S" : ""}
            </Typography>
          </Box>
        )}
      </Box>

      {taskError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTaskError("")}>
          {taskError}
        </Alert>
      )}

      {loadingTasks && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loadingTasks && activeTasks.length === 0 && (
        <Box
          sx={{
            border: "1px solid #D1D5DB",
            borderLeft: "4px solid #10B981",
            borderRadius: "8px",
            background: "#F8FAFC",
            py: 3, px: 2.5, mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: 11, fontWeight: 800, letterSpacing: "0.1em",
              textTransform: "uppercase", color: "#374151", mb: 0.5,
            }}
          >
            ON SHIFT — NO ACTIVE TASKS
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mb: 2, lineHeight: 1.5 }}>
            Polling for dispatch updates.
            {lastSyncedLabel ? ` Last synced ${lastSyncedLabel}.` : ""}
            {isSupportView && <Typography variant="caption" sx={{ color: "#6B7280", fontStyle: "italic", mt: 1, display: "block" }}>
              Read-only view - changes made by {effectiveDriverName} will not reflect here until next refresh.
            </Typography>}
          </Typography>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => fetchTasks()}
            sx={{ fontWeight: 700, borderRadius: "6px" }}
          >
            Refresh Tasks
          </MDButton>
        </Box>
      )}

      {!loadingTasks && (isSupportView ? supportViewActiveTasks : activeTasks).map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          onAction={handleStatusAction}
          onDeliverClick={setPodTask}
          onPickupDeliverClick={setPickupPodTask}
          onRequestOverride={(taskRow, type) => setOverrideDialog({ task: taskRow, type })}
          isUpdating={updatingId === task.id}
          blockedPickupReason={blockedPickup.taskId === task.id ? blockedPickup.reason : ""}
          blockedArrivalReason={blockedArrival.taskId === task.id ? blockedArrival.reason : ""}
          blockedReason={blockedDelivery.taskId === task.id ? blockedDelivery.reason : ""}
        />
      ))}

      {(isSupportView ? supportViewVaultTasks : vaultTasks).length > 0 && (
        <Box mt={3} mb={1.5} display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ flex: 1, height: "1px", background: "#E2E8F0" }} />
          <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em",
            textTransform: "uppercase", color: "#94A3B8", whiteSpace: "nowrap" }}>
            DELIVERY VAULT · {vaultTasks.length}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: "#E2E8F0" }} />
        </Box>
      )}
      {(isSupportView ? supportViewVaultTasks : vaultTasks).map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          onAction={handleStatusAction}
          onDeliverClick={setPodTask}
          onPickupDeliverClick={setPickupPodTask}
          onRequestOverride={(taskRow, type) => setOverrideDialog({ task: taskRow, type })}
          isUpdating={false}
        />
      ))}

      {/* Fixed bottom bar */}
      <Box sx={{
        position: "fixed", bottom: 0, left: 0, width: "100%", zIndex: 1000,
        background: "rgba(15,23,42,0.97)", backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(255,255,255,0.08)",
        px: 2, py: 1.5, display: "flex", gap: 1.5,
      }}>
        <MDButton
          variant="outlined" fullWidth onClick={() => fetchTasks()}
          sx={{ fontWeight: 700, fontSize: 13, color: "#E2E8F0",
            borderColor: "rgba(255,255,255,0.2)", borderRadius: 2, py: 1.25,
            "&:hover": { borderColor: "rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.06)" } }}>
          Refresh Tasks
        </MDButton>
        <MDButton
          variant="gradient" color="error" fullWidth
          onClick={() => { if (!isSupportView) { setPhase("ending"); setShiftError(""); } }}
          sx={{ fontWeight: 700, fontSize: 13, borderRadius: 2, py: 1.25 }}>
          End Shift
        </MDButton>
      </Box>

      {/* Pickup Proof Modal (two-leg: Arrived (PU) → Picked Up) */}
      {!isSupportView && (
      <>
      <PickupProofModal
        task={pickupPodTask}
        onClose={() => setPickupPodTask(null)}
        onPickedUp={handlePickupPODConfirmed}
      />

      {/* Delivery POD Modal (Arrived → Delivered) */}
      <PODModal
        task={podTask}
        onClose={() => setPodTask(null)}
        onDelivered={handlePODDelivered}
        onRequestOverride={(taskRow, type) => setOverrideDialog({ task: taskRow, type })}
      />
      <OverrideRequestDialog
        task={overrideDialog.task}
        overrideType={overrideDialog.type}
        onClose={() => setOverrideDialog({ task: null, type: "" })}
        onSubmitted={handleOverrideRequested}
      />
      </>
      )}

      {Toast}
    </Box>
  );
}
